import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { 
  Calculator, Search, X, Check, MessageCircle, Phone, Mail, 
  User, Building2, FileText, Ship, Truck, RefreshCw, 
  ChevronRight, CheckCircle, AlertTriangle, Globe, Plane, DollarSign,
  BarChart3, TrendingUp, Target, ArrowLeft
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "+8616628526361";

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount).replace("MX$", "$");
};

const formatCurrencyUSD = (amount) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Email de contacto
const CONTACT_EMAIL = "info@dma.international";

// Analytics Dashboard Component
const AnalyticsDashboard = ({ onBack }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("analytics");
  const [dateFilter, setDateFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/leads?limit=500`);
      setLeads(response.data || []);
    } catch (err) {
      console.error("Error fetching leads:", err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate analytics
  const analytics = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);

    const leadsToday = leads.filter(l => new Date(l.created_at) >= today).length;
    const leadsThisWeek = leads.filter(l => new Date(l.created_at) >= weekAgo).length;
    const leadsThisMonth = leads.filter(l => new Date(l.created_at) >= monthAgo).length;

    // Leads by day (last 7 days)
    const leadsByDay = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
      const count = leads.filter(l => {
        const d = new Date(l.created_at);
        return d >= dayStart && d < dayEnd;
      }).length;
      leadsByDay.push({
        day: dayStart.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric' }),
        count
      });
    }

    const totalValue = leads.reduce((sum, l) => sum + (parseFloat(l.productValue) || 0), 0);
    const avgValue = leads.length > 0 ? totalValue / leads.length : 0;

    // Top tariff chapters
    const tariffCounts = {};
    leads.forEach(l => {
      if (l.tariff) {
        const chapter = l.tariff.substring(0, 4);
        tariffCounts[chapter] = (tariffCounts[chapter] || 0) + 1;
      }
    });
    const topTariffs = Object.entries(tariffCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);

    const completeLeads = leads.filter(l => l.name && l.email && l.phone).length;
    const conversionRate = leads.length > 0 ? (completeLeads / leads.length * 100).toFixed(1) : 0;

    return { leadsToday, leadsThisWeek, leadsThisMonth, leadsByDay, totalValue, avgValue, topTariffs, conversionRate, completeLeads };
  }, [leads]);

  const filteredLeads = leads.filter(lead => {
    if (dateFilter !== "all") {
      const leadDate = new Date(lead.created_at);
      const now = new Date();
      if (dateFilter === "today" && leadDate.toDateString() !== now.toDateString()) return false;
      if (dateFilter === "week" && leadDate < new Date(now - 7 * 24 * 60 * 60 * 1000)) return false;
      if (dateFilter === "month" && leadDate < new Date(now - 30 * 24 * 60 * 60 * 1000)) return false;
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return lead.name?.toLowerCase().includes(term) || lead.email?.toLowerCase().includes(term) || lead.phone?.toLowerCase().includes(term);
    }
    return true;
  });

  const exportToCSV = () => {
    const headers = ["Nombre", "Email", "Teléfono", "Empresa", "Fracción", "Valor USD", "Fecha"];
    const rows = filteredLeads.map(lead => [
      lead.name || "", lead.email || "", lead.phone || "", lead.company || "",
      lead.tariff || "", lead.productValue || "", new Date(lead.created_at).toLocaleString("es-MX")
    ]);
    const csvContent = [headers.join(","), ...rows.map(row => row.map(cell => `"${cell}"`).join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `leads_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const maxDayCount = Math.max(...analytics.leadsByDay.map(d => d.count), 1);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '24px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button onClick={onBack} style={{
              padding: '10px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              background: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}>
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 style={{ margin: 0, fontSize: '24px', color: '#1a1a2e' }}>Dashboard de Analytics</h1>
              <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>DMA International - Leads</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={fetchLeads} disabled={loading} style={{
              padding: '10px 16px', border: '1px solid #e5e7eb', borderRadius: '8px',
              background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
            }}>
              <RefreshCw size={16} className={loading ? "spin" : ""} /> Actualizar
            </button>
            <button onClick={exportToCSV} style={{
              padding: '10px 16px', border: 'none', borderRadius: '8px',
              background: '#1a1a2e', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
            }}>
              <FileText size={16} /> Exportar CSV
            </button>
          </div>
        </div>

        {/* View Toggle */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          <button onClick={() => setActiveView('analytics')} style={{
            padding: '10px 20px', border: 'none', borderRadius: '8px',
            background: activeView === 'analytics' ? '#1a1a2e' : '#e5e7eb',
            color: activeView === 'analytics' ? 'white' : '#4b5563',
            fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            <BarChart3 size={16} /> Analytics
          </button>
          <button onClick={() => setActiveView('table')} style={{
            padding: '10px 20px', border: 'none', borderRadius: '8px',
            background: activeView === 'table' ? '#1a1a2e' : '#e5e7eb',
            color: activeView === 'table' ? 'white' : '#4b5563',
            fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            <User size={16} /> Lista de Leads
          </button>
        </div>

        {activeView === 'analytics' ? (
          <>
            {/* Stats Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
              <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #152a47 100%)', borderRadius: '12px', padding: '20px', color: 'white' }}>
                <div style={{ fontSize: '32px', fontWeight: '700' }}>{leads.length}</div>
                <div style={{ fontSize: '14px', opacity: 0.85 }}>Total Leads</div>
              </div>
              <div style={{ background: 'white', border: '2px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#059669' }}>{analytics.leadsToday}</div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Hoy</div>
              </div>
              <div style={{ background: 'white', border: '2px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#d97706' }}>{analytics.leadsThisWeek}</div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Esta Semana</div>
              </div>
              <div style={{ background: 'white', border: '2px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#7c3aed' }}>{analytics.leadsThisMonth}</div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>Este Mes</div>
              </div>
            </div>

            {/* Charts Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '24px' }}>
              {/* Leads by Day */}
              <div style={{ background: 'white', border: '2px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <TrendingUp size={18} /> Leads Últimos 7 Días
                </h3>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '120px' }}>
                  {analytics.leadsByDay.map((day, i) => (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{
                        width: '100%', height: `${(day.count / maxDayCount) * 100}px`,
                        background: day.count > 0 ? 'linear-gradient(180deg, #1a1a2e 0%, #3b82f6 100%)' : '#e5e7eb',
                        borderRadius: '4px 4px 0 0', minHeight: '4px'
                      }} />
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px' }}>{day.day}</div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>{day.count}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Value Stats */}
              <div style={{ background: 'white', border: '2px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DollarSign size={18} /> Valor Estimado
                </h3>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#059669' }}>${analytics.totalValue.toLocaleString('en-US')}</div>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>Valor total USD</div>
                </div>
                <div style={{ paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#1a1a2e' }}>${analytics.avgValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>Promedio por lead</div>
                </div>
              </div>
            </div>

            {/* Bottom Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Top Tariffs */}
              <div style={{ background: 'white', border: '2px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FileText size={18} /> Capítulos Más Buscados
                </h3>
                {analytics.topTariffs.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {analytics.topTariffs.map(([code, count], i) => (
                      <div key={code} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '28px', height: '28px', borderRadius: '6px',
                          background: i === 0 ? '#1a1a2e' : '#e5e7eb',
                          color: i === 0 ? 'white' : '#4b5563',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '600'
                        }}>{i + 1}</div>
                        <div style={{ flex: 1, fontFamily: 'monospace', fontWeight: '600', color: '#1a1a2e' }}>{code}...</div>
                        <div style={{ background: '#f0fdf4', color: '#059669', padding: '4px 10px', borderRadius: '12px', fontSize: '13px', fontWeight: '600' }}>{count} leads</div>
                      </div>
                    ))}
                  </div>
                ) : <p style={{ color: '#9ca3af', fontSize: '14px' }}>Sin datos aún</p>}
              </div>

              {/* Conversion */}
              <div style={{ background: 'white', border: '2px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Target size={18} /> Calidad de Leads
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <div style={{
                    width: '80px', height: '80px', borderRadius: '50%',
                    background: `conic-gradient(#059669 ${analytics.conversionRate}%, #e5e7eb ${analytics.conversionRate}%)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <div style={{
                      width: '60px', height: '60px', borderRadius: '50%', background: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: '#059669'
                    }}>{analytics.conversionRate}%</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>Leads completos</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a2e' }}>{analytics.completeLeads} / {leads.length}</div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Table View */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
              <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                <option value="all">Todos</option>
                <option value="today">Hoy</option>
                <option value="week">Última semana</option>
                <option value="month">Último mes</option>
              </select>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                <Search size={16} style={{ color: '#9ca3af' }} />
                <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #e5e7eb' }} />
              </div>
            </div>

            <div style={{ background: 'white', borderRadius: '12px', border: '2px solid #e5e7eb', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }}>Nombre</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }}>Email</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }}>Teléfono</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }}>Empresa</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }}>Fracción</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }}>Valor</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }}>Fecha</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#4b5563', borderBottom: '2px solid #e5e7eb' }}>WhatsApp</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead) => (
                    <tr key={lead.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px', fontWeight: '500' }}>{lead.name || "-"}</td>
                      <td style={{ padding: '12px' }}><a href={`mailto:${lead.email}`} style={{ color: '#3b82f6' }}>{lead.email || "-"}</a></td>
                      <td style={{ padding: '12px' }}>{lead.phone || "-"}</td>
                      <td style={{ padding: '12px', color: '#6b7280' }}>{lead.company || "-"}</td>
                      <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '13px' }}>{lead.tariff || "-"}</td>
                      <td style={{ padding: '12px', fontWeight: '600', color: '#059669' }}>{lead.productValue ? `$${parseInt(lead.productValue).toLocaleString()}` : "-"}</td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#6b7280' }}>{new Date(lead.created_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <a href={`https://wa.me/${lead.phone?.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hola ${lead.name}, gracias por usar nuestra calculadora.`)}`}
                          target="_blank" rel="noopener noreferrer"
                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', background: '#25D366', color: 'white', borderRadius: '6px' }}>
                          <MessageCircle size={16} />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredLeads.length === 0 && (
                <div style={{ padding: '48px', textAlign: 'center', color: '#9ca3af' }}>
                  <User size={48} style={{ marginBottom: '12px' }} />
                  <p>No hay leads que mostrar</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Main Lead Calculator Component
const LeadCalculator = () => {
  // Check for admin mode - must be first
  const [isAdminMode, setIsAdminMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('admin') === 'true';
  });

  // Form state - ALL hooks must be declared before any conditional returns
  const [step, setStep] = useState(1);
  const [tariffSearch, setTariffSearch] = useState("");
  const [tariffResults, setTariffResults] = useState([]);
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const [formData, setFormData] = useState({
    productValue: "",
    originCountry: "China",
    incoterm: "FOB",
    transportType: "maritimo",
    units: "1",
    freightCost: "",
    insuranceCost: ""
  });

  const [leadData, setLeadData] = useState({
    name: "",
    email: "",
    phone: "",
    company: ""
  });

  const [exchangeRate, setExchangeRate] = useState(17.5);
  const [banxicoInfo, setBanxicoInfo] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  // Fetch exchange rate from Banxico
  useEffect(() => {
    const fetchRate = async () => {
      try {
        const response = await axios.get(`${API}/banxico/exchange-rate`);
        if (response.data?.rate) {
          setExchangeRate(response.data.rate);
          setBanxicoInfo({
            rate: response.data.rate,
            date: response.data.date,
            source: response.data.source,
            success: response.data.success
          });
        }
      } catch (err) {
        console.log("Using default exchange rate");
        setBanxicoInfo({
          rate: 17.5,
          date: new Date().toLocaleDateString('es-MX'),
          source: 'Referencial',
          success: false
        });
      }
    };
    fetchRate();
  }, []);

  // Search tariffs with debounce
  useEffect(() => {
    if (tariffSearch.length < 2 || selectedTariff) return;
    
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await axios.get(`${API}/tariffs/search?q=${encodeURIComponent(tariffSearch)}&limit=8`);
        setTariffResults(response.data?.results || []);
        setShowDropdown(true);
      } catch (err) {
        console.error("Search error:", err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [tariffSearch, selectedTariff]);

  // Calculate estimate in real-time
  const estimate = useMemo(() => {
    const value = parseFloat(formData.productValue) || 0;
    if (value <= 0) return null;

    const valueMXN = value * exchangeRate;
    
    // Freight and insurance (only for non-CIF)
    const freight = formData.incoterm !== 'CIF' ? (parseFloat(formData.freightCost) || 0) : 0;
    const insurance = formData.incoterm !== 'CIF' ? (parseFloat(formData.insuranceCost) || 0) : 0;
    const freightMXN = freight * exchangeRate;
    const insuranceMXN = insurance * exchangeRate;
    
    // Customs value = product + freight + insurance
    const customsValue = valueMXN + freightMXN + insuranceMXN;
    
    // Handle exempt products (igi_rate = 0) and when no tariff selected (default 15%)
    const igiRate = selectedTariff !== null 
      ? (selectedTariff.igi_rate || 0) / 100 
      : 0.15;
    const igi = customsValue * igiRate;
    const dta = customsValue * 0.008;
    const ivaBase = customsValue + igi + dta;
    const iva = ivaBase * 0.16;
    
    // Logistic estimate (only if no freight specified)
    const logisticEstimate = freight > 0 ? 0 : (value * 0.08 * exchangeRate);
    const total = customsValue + igi + dta + iva + logisticEstimate;

    return {
      valueMXN,
      freightMXN,
      insuranceMXN,
      customsValue,
      igi,
      igiRate: igiRate * 100, // for display
      iva,
      dta,
      logistics: logisticEstimate,
      total,
      totalUSD: total / exchangeRate,
      isExempt: selectedTariff?.is_exempt || false,
      hasFreight: freight > 0
    };
  }, [formData, selectedTariff, exchangeRate]);

  const selectTariff = (tariff) => {
    setSelectedTariff(tariff);
    setTariffSearch(tariff.code + " - " + tariff.description?.substring(0, 50));
    setShowDropdown(false);
  };

  const clearTariff = () => {
    setSelectedTariff(null);
    setTariffSearch("");
    setTariffResults([]);
  };

  const handleSubmitLead = async () => {
    if (!leadData.name || !leadData.email || !leadData.phone) {
      alert("Por favor completa todos los campos requeridos");
      return;
    }

    setLoading(true);
    try {
      // Save lead with calculation data
      await axios.post(`${API}/leads`, {
        ...leadData,
        calculation: estimate,
        tariff: selectedTariff?.code,
        productValue: formData.productValue,
        source: "calculator_quote_request"
      });
      setLeadSubmitted(true);
    } catch (err) {
      console.error("Error submitting lead:", err);
      alert("Error al enviar. Por favor intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  // If admin mode, show Analytics Dashboard (after all hooks)
  if (isAdminMode) {
    return <AnalyticsDashboard onBack={() => {
      window.history.pushState({}, '', window.location.pathname);
      setIsAdminMode(false);
    }} />;
  }

  return (
    <div className="lead-calc-container">
      {/* Header */}
      <header className="lead-calc-header" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)' }}>
        <div style={{ marginBottom: '8px' }}>
          <h1 style={{ 
            fontSize: '64px', 
            fontWeight: '700', 
            margin: '0',
            letterSpacing: '-2px'
          }}>
            arancel<span style={{ color: '#5BC0BE' }}>.ai</span>
          </h1>
          <p style={{ 
            fontSize: '12px', 
            opacity: 0.7, 
            marginTop: '8px',
            fontWeight: '400'
          }}>
            powered by <a href="https://www.dma.international" target="_blank" rel="noopener noreferrer" style={{ color: '#5BC0BE', textDecoration: 'none' }}>DMA International</a>
          </p>
        </div>
        <p style={{ 
          fontSize: '18px', 
          fontWeight: '400', 
          letterSpacing: '0.3px',
          opacity: 0.95,
          maxWidth: '600px',
          margin: '16px auto 0'
        }}>
          Calcula el costo real de importar a México
        </p>
        
        <div className="lead-calc-features">
          <span><Check size={16} /> Basado en TIGIE</span>
          <span><Check size={16} /> Estimación de IGI e IVA</span>
          <span><Check size={16} /> +1000 simulaciones realizadas</span>
        </div>

        {/* Contact Info */}
        <div style={{
          marginTop: '20px',
          display: 'flex',
          justifyContent: 'center',
          gap: '24px',
          flexWrap: 'wrap'
        }}>
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#5BC0BE',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#25D366">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            +86 166 2852 6361
          </span>
          <a href="mailto:info@dma.international" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#5BC0BE',
            textDecoration: 'none',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            <Mail size={16} />
            info@dma.international
          </a>
        </div>
      </header>

      {/* How it works */}
      <div className="how-it-works">
        <h3>¿Cómo funciona?</h3>
        <div className="steps">
          <div className={`step ${step >= 1 ? 'active' : ''}`}>
            <div className="step-number">1</div>
            <span>Busca tu producto</span>
            <small>Encuentra la fracción arancelaria correcta</small>
          </div>
          <div className={`step ${step >= 2 ? 'active' : ''}`}>
            <div className="step-number">2</div>
            <span>Ingresa detalles</span>
            <small>Valor, país de origen e Incoterm</small>
          </div>
          <div className={`step ${step >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <span>Obtén tu cotización</span>
            <small>Te contactamos con el costo final</small>
          </div>
        </div>
      </div>

      {/* Import Journey Animation */}
      <div className="import-journey" style={{
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: '16px',
        padding: '24px 12px',
        margin: '24px auto 32px',
        maxWidth: '1050px',
        width: 'calc(100% - 48px)',
        position: 'relative',
        overflow: 'hidden',
        marginLeft: 'auto',
        marginRight: 'auto'
      }}>
        <p style={{
          textAlign: 'center',
          fontSize: '13px',
          color: '#1a1a2e',
          marginBottom: '20px',
          fontWeight: '600'
        }}>
          Recorrido de tu mercancía
        </p>
        
        {/* Journey Line */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          padding: '0 5px'
        }}>
          {/* Background Line */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '30px',
            right: '30px',
            height: '3px',
            background: '#cbd5e1',
            transform: 'translateY(-50%)',
            zIndex: 0
          }} />
          
          {/* Animated Progress Line - hasta Puerto MX */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '30px',
            width: '58%',
            height: '3px',
            background: 'linear-gradient(90deg, #577590 0%, #5BC0BE 100%)',
            transform: 'translateY(-50%)',
            zIndex: 1,
            animation: 'pulse 2s ease-in-out infinite'
          }} />
          
          {/* 1. Fábrica */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2 }}>
            <div style={{
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: '#577590',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: '0 3px 10px rgba(87,117,144,0.3)'
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2 20h20M4 20V10l4-4v4l4-4v4l4-4v14M14 20v-4h4v4" />
              </svg>
            </div>
            <span style={{ fontSize: '9px', color: '#475569', marginTop: '5px', fontWeight: '500', textAlign: 'center' }}>Fábrica</span>
          </div>

          {/* 2. Transporte */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2 }}>
            <div style={{
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: '#577590',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: '0 3px 10px rgba(87,117,144,0.3)'
            }}>
              <Truck size={18} />
            </div>
            <span style={{ fontSize: '9px', color: '#475569', marginTop: '5px', fontWeight: '500', textAlign: 'center' }}>Transporte</span>
          </div>

          {/* 3. Papeleo Exportación */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2 }}>
            <div style={{
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: '#577590',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: '0 3px 10px rgba(87,117,144,0.3)'
            }}>
              <FileText size={18} />
            </div>
            <span style={{ fontSize: '9px', color: '#475569', marginTop: '5px', fontWeight: '500', textAlign: 'center', lineHeight: '1.2' }}>Papeleo<br/>Export</span>
          </div>

          {/* 4. Puerto Origen */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2 }}>
            <div style={{
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: '#577590',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: '0 3px 10px rgba(87,117,144,0.3)'
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 17h14M3 21h18M4 17l1-6h14l1 6" />
              </svg>
            </div>
            <span style={{ fontSize: '9px', color: '#475569', marginTop: '5px', fontWeight: '500', textAlign: 'center', lineHeight: '1.2' }}>Puerto<br/>Origen</span>
          </div>

          {/* 5. Barco Cruzando */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2, position: 'relative' }}>
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #5BC0BE 0%, #3AAFA9 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: '0 4px 14px rgba(91,192,190,0.4)',
              animation: 'float 3s ease-in-out infinite'
            }}>
              <Ship size={22} />
            </div>
            <span style={{ fontSize: '9px', color: '#5BC0BE', marginTop: '5px', fontWeight: '600' }}>Cruzando</span>
            <div style={{
              position: 'absolute',
              bottom: '18px',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '8px',
              color: '#5BC0BE',
              opacity: 0.5,
              animation: 'wave 1.5s ease-in-out infinite'
            }}>〰️〰️</div>
          </div>

          {/* 6. Puerto MX - Donde termina la cotización */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2, position: 'relative' }}>
            <div style={{
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #5BC0BE 0%, #3AAFA9 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: '0 4px 12px rgba(91,192,190,0.4)',
              border: '3px solid #d1fae5'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 17h14M3 21h18M4 17l1-6h14l1 6" />
              </svg>
            </div>
            <span style={{ fontSize: '9px', color: '#059669', marginTop: '5px', fontWeight: '700', textAlign: 'center', lineHeight: '1.2' }}>Puerto<br/>MX</span>
            <div style={{
              position: 'absolute',
              top: '-6px',
              right: '-12px',
              background: '#059669',
              color: 'white',
              fontSize: '7px',
              padding: '2px 4px',
              borderRadius: '6px',
              fontWeight: '700'
            }}>✓</div>
          </div>

          {/* 7. Papeleo Importación MX (Grayed) */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2, opacity: 0.45 }}>
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: '#94a3b8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              border: '2px dashed #cbd5e1'
            }}>
              <FileText size={16} />
            </div>
            <span style={{ fontSize: '8px', color: '#94a3b8', marginTop: '5px', textAlign: 'center', lineHeight: '1.2' }}>Papeleo<br/>Import</span>
          </div>

          {/* 8. Flete Interno (Grayed) */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2, opacity: 0.45 }}>
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: '#94a3b8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              border: '2px dashed #cbd5e1'
            }}>
              <Truck size={16} />
            </div>
            <span style={{ fontSize: '8px', color: '#94a3b8', marginTop: '5px', textAlign: 'center', lineHeight: '1.2' }}>Flete<br/>Interno</span>
          </div>

          {/* 9. Tu Bodega (Grayed) */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2, opacity: 0.45 }}>
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: '#94a3b8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              border: '2px dashed #cbd5e1'
            }}>
              <Building2 size={16} />
            </div>
            <span style={{ fontSize: '8px', color: '#94a3b8', marginTop: '5px', textAlign: 'center', lineHeight: '1.2' }}>Tu<br/>Bodega</span>
          </div>
        </div>

        {/* Info Note */}
        <div style={{
          marginTop: '16px',
          padding: '10px 16px',
          background: 'rgba(255,255,255,0.7)',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <AlertTriangle size={14} style={{ color: '#d97706', flexShrink: 0 }} />
          <span style={{ fontSize: '12px', color: '#78716c' }}>
            Esta cotización incluye hasta <strong style={{ color: '#059669' }}>puerto en México</strong>. 
            Para el resto del proceso <a href="mailto:info@dma.international?subject=Cotización completa importación&body=Hola, necesito cotización completa incluyendo despacho aduanal y flete interno hasta mi bodega" style={{ color: '#1a1a2e', fontWeight: '600' }}>contáctanos</a>.
          </span>
        </div>

        {/* CSS Animations */}
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-4px); }
          }
          @keyframes wave {
            0%, 100% { transform: translateX(-50%) translateY(0); }
            50% { transform: translateX(-50%) translateY(2px); }
          }
        `}</style>
      </div>

      <div className="lead-calc-main">
        {/* Left: Calculator Form */}
        <div className="lead-calc-form-area">
          <div className="lead-calc-card">
            <h2><Calculator size={20} /> Calcula los costos de importación</h2>
            
            {/* Tariff Search */}
            <div className="form-field" style={{ position: 'relative' }}>
              <label><FileText size={16} /> Producto o fracción arancelaria</label>
              <div className="search-input-wrapper">
                <Search size={18} />
                <input
                  type="text"
                  value={tariffSearch}
                  onChange={(e) => {
                    setTariffSearch(e.target.value);
                    if (selectedTariff) setSelectedTariff(null);
                  }}
                  onFocus={() => tariffResults.length > 0 && setShowDropdown(true)}
                  placeholder="Ej: 8471.30, celulares, textil..."
                  data-testid="tariff-search"
                />
                {searchLoading && <RefreshCw size={18} className="spin" />}
                {selectedTariff && (
                  <button className="clear-btn" onClick={clearTariff}>
                    <X size={16} />
                  </button>
                )}
              </div>
              
              {showDropdown && tariffResults.length > 0 && (
                <div className="tariff-dropdown" style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  background: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  marginTop: '4px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
                }}>
                  {tariffResults.map((tariff, i) => (
                    <div 
                      key={i} 
                      className="tariff-option"
                      onClick={() => selectTariff(tariff)}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f1f5f9',
                        transition: 'background 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                      onMouseLeave={(e) => e.target.style.background = 'white'}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1a1a2e' }}>{tariff.code}</span>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: tariff.is_exempt ? '#059669' : '#f59e0b' }}>
                          {tariff.is_exempt ? "Exento" : `IGI: ${tariff.igi_rate || 0}%`}
                        </span>
                      </div>
                      <p style={{ fontSize: '13px', color: '#6b7280', margin: 0, lineHeight: 1.4 }}>
                        {tariff.description?.substring(0, 80)}...
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {selectedTariff && (
                <>
                  <div className="selected-tariff">
                    <CheckCircle size={16} />
                    <span>{selectedTariff.code}</span>
                    <span className="rate">IGI: {selectedTariff.igi_rate || 0}%</span>
                  </div>
                  {/* NOM CTA sutil - mostrar si el producto podría requerir NOM */}
                  {(() => {
                    const code = selectedTariff.code || "";
                    // Obtener el capítulo (primeros 2 dígitos del código arancelario)
                    const chapter = parseInt(code.substring(0, 2), 10);
                    // Capítulos que típicamente requieren NOM: 
                    // 02-22: Alimentos y bebidas
                    // 33: Cosméticos
                    // 61-63: Textiles y prendas de vestir
                    // 64: Calzado
                    // 84-85: Eléctricos y electrónicos
                    // 90: Instrumentos médicos
                    // 94: Iluminación/muebles
                    // 95: Juguetes
                    const nomChapters = [
                      2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, // Alimentos
                      33, // Cosméticos
                      61, 62, 63, // Textiles
                      64, // Calzado
                      84, 85, // Eléctricos/electrónicos
                      90, // Médicos
                      94, // Iluminación
                      95  // Juguetes
                    ];
                    const mayRequireNom = nomChapters.includes(chapter);
                    
                    if (mayRequireNom && !selectedTariff.is_prohibited) {
                      return (
                        <div 
                          data-testid="nom-inline-cta"
                          style={{
                            marginTop: '10px',
                            padding: '10px 14px',
                            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '10px',
                            border: '1px solid #f59e0b'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            <Shield size={16} style={{ color: '#d97706', flexShrink: 0 }} />
                            <span style={{ fontSize: '12px', color: '#92400e', lineHeight: '1.3' }}>
                              Este producto podría requerir <strong>certificación NOM</strong>
                            </span>
                          </div>
                          <a 
                            href={`mailto:info@dma.international?subject=Verificar NOM para ${selectedTariff.code}&body=Hola, quiero verificar si mi producto (${selectedTariff.code}) requiere NOM y cotizar el trámite con DMA International.`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              padding: '5px 10px',
                              background: '#1a1a2e',
                              color: 'white',
                              borderRadius: '5px',
                              fontSize: '11px',
                              fontWeight: '600',
                              textDecoration: 'none',
                              whiteSpace: 'nowrap'
                            }}
                            data-testid="nom-cta-button"
                          >
                            <Mail size={12} />
                            DMA International tramita tu NOM
                          </a>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </>
              )}
            </div>

            {/* Value */}
            <div className="form-field">
              <label><Building2 size={16} /> Valor de la mercancía (USD)</label>
              <input
                type="number"
                value={formData.productValue}
                onChange={(e) => setFormData({ ...formData, productValue: e.target.value })}
                placeholder="Ej: 10,000"
                data-testid="product-value"
              />
            </div>

            {/* Country */}
            <div className="form-field">
              <label><Globe size={16} /> País de origen</label>
              <select 
                value={formData.originCountry}
                onChange={(e) => setFormData({ ...formData, originCountry: e.target.value })}
              >
                <option value="China">China</option>
                <option value="USA">Estados Unidos</option>
                <option value="Germany">Alemania</option>
                <option value="Japan">Japón</option>
                <option value="Korea">Corea del Sur</option>
                <option value="Other">Otro</option>
              </select>
            </div>

            {/* Incoterm Section */}
            <div className="form-field">
              <label><Ship size={16} /> Término de Venta (Incoterm)</label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '8px',
                marginTop: '8px'
              }}>
                {[
                  { value: 'FOB', desc: 'Vendedor entrega en puerto de origen', dmaNote: null },
                  { value: 'CIF', desc: 'Flete y seguro incluidos hasta puerto destino', dmaNote: null },
                  { value: 'FCA', desc: 'Vendedor entrega al transportista designado', dmaNote: 'DMA International gestiona exportación China-México' },
                  { value: 'EXW', desc: 'Comprador asume todo desde fábrica', dmaNote: 'DMA International gestiona exportación + flete local en China' }
                ].map(inc => (
                  <div
                    key={inc.value}
                    onClick={() => setFormData({ ...formData, incoterm: inc.value })}
                    style={{
                      padding: '12px',
                      border: formData.incoterm === inc.value ? '2px solid #1a1a2e' : '1px solid #e5e7eb',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: formData.incoterm === inc.value ? '#f0f9ff' : 'white',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div style={{ 
                      fontSize: '15px', 
                      fontWeight: '700', 
                      color: formData.incoterm === inc.value ? '#1a1a2e' : '#4b5563',
                      marginBottom: '4px'
                    }}>
                      {inc.value}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: '1.3' }}>
                      {inc.desc}
                    </div>
                    {inc.dmaNote && (
                      <div style={{ 
                        fontSize: '10px', 
                        color: '#f59e0b', 
                        marginTop: '6px',
                        fontWeight: '500'
                      }}>
                        ★ {inc.dmaNote}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* DMA Export Service Note for FCA/EXW */}
            {(formData.incoterm === 'FCA' || formData.incoterm === 'EXW') && (
              <div style={{
                background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                border: '1px solid #fcd34d',
                borderRadius: '8px',
                padding: '12px 14px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}>
                <Truck size={18} style={{ color: '#d97706', flexShrink: 0, marginTop: '2px' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>
                    {formData.incoterm === 'EXW' 
                      ? '¿Necesitas flete de fábrica a puerto en China?' 
                      : '¿Necesitas apoyo con el papeleo de exportación?'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#a16207', lineHeight: '1.4' }}>
                    {formData.incoterm === 'EXW' 
                      ? 'DMA International gestiona el flete local en China (de fábrica a puerto) y todo el papeleo de exportación China-México.'
                      : 'DMA International puede encargarse del papeleo de exportación de China a México por ti.'}
                  </div>
                  <a 
                    href={`mailto:info@dma.international?subject=Apoyo con ${formData.incoterm}&body=${encodeURIComponent(
                      formData.incoterm === 'EXW'
                        ? 'Hola, tengo una compra EXW y necesito apoyo con el flete local en China y el papeleo de exportación.'
                        : 'Hola, tengo una compra FCA y necesito apoyo con el papeleo de exportación de China a México.'
                    )}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginTop: '8px',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#1a1a2e',
                      textDecoration: 'none'
                    }}
                  >
                    Solicitar apoyo <ChevronRight size={14} />
                  </a>
                </div>
              </div>
            )}

            {/* Freight & Insurance - Only show for FOB, FCA, EXW */}
            {formData.incoterm !== 'CIF' && (
              <div style={{
                background: '#f8fafc',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{ 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: '#6b7280', 
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <Truck size={14} />
                  Costos de Flete y Seguro (USD)
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>
                      Flete Internacional
                    </label>
                    <input
                      type="number"
                      value={formData.freightCost || ''}
                      onChange={(e) => setFormData({ ...formData, freightCost: e.target.value })}
                      placeholder="0"
                      data-testid="freight-cost"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>
                      Seguro de Carga
                    </label>
                    <input
                      type="number"
                      value={formData.insuranceCost || ''}
                      onChange={(e) => setFormData({ ...formData, insuranceCost: e.target.value })}
                      placeholder="0"
                      data-testid="insurance-cost"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
                <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px', marginBottom: 0 }}>
                  Si no tienes estos costos, DMA International puede cotizarlos.
                </p>
              </div>
            )}

            {/* CIF Note */}
            {formData.incoterm === 'CIF' && (
              <div style={{
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <CheckCircle size={18} style={{ color: '#059669', flexShrink: 0 }} />
                <span style={{ fontSize: '13px', color: '#047857' }}>
                  El flete y seguro ya están incluidos en el valor CIF de tu factura.
                </span>
              </div>
            )}

            {/* Transport */}
            <div className="form-field">
              <label>Transporte</label>
              <div className="transport-options">
                <button 
                  className={formData.transportType === 'maritimo' ? 'active' : ''}
                  onClick={() => setFormData({ ...formData, transportType: 'maritimo' })}
                >
                  <Ship size={18} /> Marítimo
                </button>
                <button 
                  className={formData.transportType === 'aereo' ? 'active' : ''}
                  onClick={() => setFormData({ ...formData, transportType: 'aereo' })}
                >
                  <Plane size={18} /> Aéreo
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Results */}
        <div className="lead-calc-results-area">
          {/* Exchange Rate Display */}
          {banxicoInfo && (
            <div style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '14px 18px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <DollarSign size={18} style={{ color: '#5BC0BE' }} />
                <div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>Tipo de cambio {banxicoInfo.source}</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e' }}>
                    1 USD = ${banxicoInfo.rate.toFixed(4)} MXN
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '10px', color: '#9ca3af' }}>{banxicoInfo.date}</div>
                <a 
                  href="https://www.banxico.org.mx/tipcamb/main.do?page=tip&idioma=sp"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: '10px', color: '#5BC0BE', textDecoration: 'none' }}
                >
                  Ver en Banxico →
                </a>
              </div>
            </div>
          )}

          {/* Live Estimate */}
          <div className="lead-calc-card estimate-card">
            <h2 style={{ fontSize: '16px' }}>Tu inversión estimada <span style={{ fontWeight: '400', color: '#6b7280' }}>en Pesos Mexicanos</span></h2>
            
            {estimate ? (
              <>
                <div className="estimate-rows">
                  <div className="estimate-row">
                    <span>Valor mercancía</span>
                    <span>{formatCurrency(estimate.valueMXN)} MXN</span>
                  </div>
                  {estimate.freightMXN > 0 && (
                    <div className="estimate-row">
                      <span>Flete internacional</span>
                      <span>{formatCurrency(estimate.freightMXN)} MXN</span>
                    </div>
                  )}
                  {estimate.insuranceMXN > 0 && (
                    <div className="estimate-row">
                      <span>Seguro de carga</span>
                      <span>{formatCurrency(estimate.insuranceMXN)} MXN</span>
                    </div>
                  )}
                  <div className="estimate-row">
                    <span>
                      IGI ({estimate.igiRate}%)
                      {estimate.isExempt && <span style={{ color: '#059669', fontSize: '11px', marginLeft: '6px' }}>Exento</span>}
                    </span>
                    <span style={estimate.isExempt ? { color: '#059669' } : {}}>{formatCurrency(estimate.igi)} MXN</span>
                  </div>
                  <div className="estimate-row">
                    <span>IVA (16%)</span>
                    <span>{formatCurrency(estimate.iva)} MXN</span>
                  </div>
                  <div className="estimate-row">
                    <span>DTA (0.8%)</span>
                    <span>{formatCurrency(estimate.dta)} MXN</span>
                  </div>
                  {estimate.logistics > 0 && (
                    <div className="estimate-row">
                      <span>Costos logísticos*</span>
                      <span>{formatCurrency(estimate.logistics)} MXN</span>
                    </div>
                  )}
                </div>
                <div className="estimate-total">
                  <span>Inversión total estimada:</span>
                  <span className="total-amount">{formatCurrency(estimate.total)} <span style={{ fontSize: '18px', fontWeight: '500' }}>MXN</span></span>
                  <span className="total-usd">≈ {formatCurrencyUSD(estimate.totalUSD)} USD</span>
                </div>
                <p className="estimate-note">
                  *Estimación referencial. El tipo de cambio final lo asigna el SAT al momento de la liberación aduanal.
                </p>
              </>
            ) : (
              <div className="estimate-empty">
                <Calculator size={32} />
                <p>Ingresa el valor de tu mercancía para ver el costo estimado en pesos mexicanos</p>
              </div>
            )}
          </div>

          {/* Lead Capture - Quote Request */}
          {!leadSubmitted ? (
            <div className="lead-calc-card quote-card">
              <h2 style={{ fontSize: '16px' }}>¿Quieres saber el costo exacto <span style={{ fontWeight: '400', color: '#6b7280' }}>hasta tu bodega?</span></h2>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>Solicita una cotización personalizada. Te llamamos para conocer tu caso y enviarte el costo real.</p>
              
              <div className="lead-form">
                <div className="form-field">
                  <input
                    type="text"
                    placeholder="Nombre completo *"
                    value={leadData.name}
                    onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                    data-testid="lead-name"
                  />
                </div>
                <div className="form-field">
                  <input
                    type="email"
                    placeholder="Correo electrónico *"
                    value={leadData.email}
                    onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                    data-testid="lead-email"
                  />
                </div>
                <div className="form-field">
                  <input
                    type="tel"
                    placeholder="Teléfono *"
                    value={leadData.phone}
                    onChange={(e) => setLeadData({ ...leadData, phone: e.target.value })}
                    data-testid="lead-phone"
                  />
                </div>
                <div className="form-field">
                  <input
                    type="text"
                    placeholder="Empresa (opcional)"
                    value={leadData.company}
                    onChange={(e) => setLeadData({ ...leadData, company: e.target.value })}
                    data-testid="lead-company"
                  />
                </div>
                <button 
                  className="submit-quote-btn"
                  onClick={handleSubmitLead}
                  disabled={loading}
                  data-testid="submit-quote"
                >
                  {loading ? (
                    <><RefreshCw size={18} className="spin" /> Enviando...</>
                  ) : (
                    <>Comenzar cálculo <ChevronRight size={18} /></>
                  )}
                </button>
              </div>
              
              <p className="lead-disclaimer">
                Esta calculadora es una estimación referencial. 
                La clasificación arancelaria final debe ser validada por un especialista.
              </p>
            </div>
          ) : (
            <div className="lead-calc-card quote-card success">
              <CheckCircle size={48} />
              <h2>¡Solicitud enviada!</h2>
              <p>Un especialista de DMA International te contactará en las próximas 24 horas para darte una cotización personalizada.</p>
              <a 
                href="mailto:info@dma.international?subject=Cotización urgente&body=Hola, acabo de solicitar una cotización en la calculadora de importación."
                className="whatsapp-quick"
              >
                <Mail size={18} /> ¿Urgente? Escríbenos
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Services Section */}
      <div className="lead-calc-services">
        <h2>Te ayudamos con tu importación</h2>
        
        <div className="services-grid">
          <div className="service-card">
            <div className="service-icon"><FileText size={24} /></div>
            <div className="service-content">
              <h3>Clasificación arancelaria</h3>
              <p>¿No estás seguro de la fracción? Nuestros especialistas te ayudan.</p>
              <a 
                href="mailto:info@dma.international?subject=Clasificación arancelaria&body=Hola, necesito ayuda con la clasificación arancelaria de mi producto."
                className="service-cta"
              >
                Clasificación profesional <ChevronRight size={16} />
              </a>
            </div>
          </div>

          <div className="service-card highlight">
            <div className="service-icon"><Shield size={24} /></div>
            <div className="service-content">
              <h3>Certificación NOM</h3>
              <p>¿Tu producto necesita NOM? Gestionamos todas las certificaciones.</p>
              <a 
                href="mailto:info@dma.international?subject=Certificación NOM&body=Hola, necesito información sobre el trámite de certificación NOM para mi producto."
                className="service-cta"
              >
                Gestionar certificación NOM <ChevronRight size={16} />
              </a>
            </div>
          </div>

          <div className="service-card">
            <div className="service-icon"><Truck size={24} /></div>
            <div className="service-content">
              <h3>Logística internacional</h3>
              <p>Cotiza flete marítimo o aéreo hasta tu bodega en México.</p>
              <a 
                href="mailto:info@dma.international?subject=Cotización flete internacional&body=Hola, necesito cotización de flete internacional hasta mi bodega."
                className="service-cta"
              >
                Cotizar transporte <ChevronRight size={16} />
              </a>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="cta-call-section">
          <div className="cta-call-content">
            <Phone size={32} />
            <div>
              <h3>¿Prefieres hablar con un especialista?</h3>
              <p>Agenda una llamada breve para conocer tu caso y darte una cotización precisa.</p>
            </div>
          </div>
          <a 
            href="mailto:info@dma.international?subject=Agendar llamada importación&body=Hola, me gustaría agendar una llamada para recibir asesoría sobre mi importación."
            className="cta-call-btn"
          >
            <Mail size={20} /> Agendar llamada
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="lead-calc-footer" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)' }}>
        <a href="https://www.dma.international" target="_blank" rel="noopener noreferrer" style={{ marginBottom: '8px', display: 'inline-block' }}>
          <img src="/dma-logo.png" alt="DMA International" style={{ 
            height: '30px', 
            width: 'auto',
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
          }} />
        </a>
        <p>Calculadora de Costos de Importación</p>
        <p className="disclaimer">Esta herramienta proporciona estimaciones referenciales. Los costos finales pueden variar.</p>
        <a href="https://www.dma.international" target="_blank" rel="noopener noreferrer" style={{ color: '#5BC0BE', fontSize: '12px', marginTop: '8px', display: 'inline-block' }}>
          www.dma.international
        </a>
      </footer>
    </div>
  );
};

// Shield icon component
const Shield = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
  </svg>
);

export default LeadCalculator;
